import { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * A table with a filter on every column, in the way a spreadsheet does it:
 * click the funnel, tick the values you want, and the table (and the export)
 * narrows to match.
 *
 * Columns are declared as:
 *   { key, label, value?(row), render?(row), type?: "date", align?, width? }
 * `value` is what gets filtered on and shown when there is no `render`;
 * `type: "date"` swaps the checkbox list for a from/to range, because a list
 * of every distinct date is useless.
 *
 * Export sends the ids of the rows that survived rather than the filter state,
 * so the file cannot disagree with what was on screen.
 */

const norm = (v) => (v === null || v === undefined ? "" : String(v));
export const dash = (v) => (norm(v).trim() === "" ? "—" : norm(v));

function Funnel({ active }) {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true"
         className={active ? "text-emerald-600" : "text-zinc-300"}>
      <path fill="currentColor" d="M1 2h14l-5.5 6.5V14l-3 1.5V8.5z" />
    </svg>
  );
}

export default function FilterableTable({
  kind, rows, columns, rowKey, onRowClick, emptyText = "Nothing here yet.",
}) {
  const [sel, setSel] = useState({});
  const [range, setRange] = useState({});
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const valueOf = (col, r) => norm(col.value ? col.value(r) : r[col.key]);
  const rawOf = (col, r) => norm(col.raw ? col.raw(r) : r[col.key]).slice(0, 10);

  const visible = useMemo(() => rows.filter((r) => {
    for (const col of columns) {
      const chosen = sel[col.key];
      if (chosen && chosen.length && !chosen.includes(valueOf(col, r))) return false;
      const rg = range[col.key];
      if (rg && (rg.from || rg.to)) {
        const raw = rawOf(col, r);
        if (!raw) return false;
        if (rg.from && raw < rg.from) return false;
        if (rg.to && raw > rg.to) return false;
      }
    }
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, columns, sel, range]);

  const distinct = (col) => {
    const seen = new Set();
    rows.forEach((r) => seen.add(valueOf(col, r)));
    return Array.from(seen).sort((a, b) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b, undefined, { numeric: true });
    });
  };

  const activeCount = columns.filter(
    (c) => (sel[c.key] && sel[c.key].length) ||
           (range[c.key] && (range[c.key].from || range[c.key].to))).length;

  const clearAll = () => { setSel({}); setRange({}); setOpen(null); };

  const toggle = (col, v) => {
    setSel((s) => {
      const cur = s[col.key] || [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...s, [col.key]: next };
    });
  };

  const exportRows = async (includeBank) => {
    if (!visible.length) {
      toast.error("Nothing to export - every row is filtered out.");
      return;
    }
    if (includeBank && !window.confirm(
      `This file will contain PAN and full bank account numbers for ` +
      `${visible.length} ${visible.length === 1 ? "person" : "people"}. Continue?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/exports/${kind}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids: visible.map((r) => rowKey(r)),
          include_bank: includeBank,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `${kind}.xlsx`;
      document.body.appendChild(a);
      a.click();
      // Revoking straight away cancels the download in some browsers.
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrap} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-500" data-testid="ft-count">
          Showing {visible.length} of {rows.length}
          {activeCount > 0 && ` · ${activeCount} filter${activeCount === 1 ? "" : "s"}`}
        </span>
        {activeCount > 0 && (
          <button onClick={clearAll}
                  className="text-xs text-emerald-700 hover:underline"
                  data-testid="ft-clear">Clear filters</button>
        )}
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => exportRows(false)} data-testid="ft-export">
            {busy ? "Preparing..." : "Export"}
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => exportRows(true)}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="ft-export-bank">
            Export with bank details
          </Button>
        </span>
      </div>

      <div className="border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              {columns.map((col) => {
                const isOpen = open === col.key;
                const chosen = sel[col.key] || [];
                const rg = range[col.key] || {};
                const active = chosen.length > 0 || rg.from || rg.to;
                const opts = col.type === "date" ? [] : distinct(col);
                const shown = search && isOpen
                  ? opts.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
                  : opts;
                return (
                  <th key={col.key}
                      className={`relative px-3 py-2 font-medium text-xs text-zinc-600 whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"}`}>
                    <button
                      className="inline-flex items-center gap-1 hover:text-zinc-900"
                      onClick={() => { setOpen(isOpen ? null : col.key); setSearch(""); }}
                      data-testid={`ft-head-${col.key}`}>
                      {col.label}
                      <Funnel active={active} />
                    </button>

                    {isOpen && (
                      <div className="absolute z-30 mt-1 left-0 w-60 bg-white border border-zinc-200 rounded-md shadow-lg p-2 font-normal"
                           data-testid={`ft-panel-${col.key}`}>
                        {col.type === "date" ? (
                          <div className="space-y-2">
                            <label className="block text-[11px] text-zinc-500">From
                              <input type="date" value={rg.from || ""}
                                     className="w-full border border-zinc-300 rounded px-2 py-1 text-xs mt-0.5"
                                     onChange={(e) => setRange((s) => ({ ...s, [col.key]: { ...rg, from: e.target.value } }))} />
                            </label>
                            <label className="block text-[11px] text-zinc-500">To
                              <input type="date" value={rg.to || ""}
                                     className="w-full border border-zinc-300 rounded px-2 py-1 text-xs mt-0.5"
                                     onChange={(e) => setRange((s) => ({ ...s, [col.key]: { ...rg, to: e.target.value } }))} />
                            </label>
                            <button className="text-[11px] text-emerald-700 hover:underline"
                                    onClick={() => setRange((s) => ({ ...s, [col.key]: {} }))}>
                              Clear this column
                            </button>
                          </div>
                        ) : (
                          <>
                            <input autoFocus value={search} placeholder="Search values"
                                   className="w-full border border-zinc-300 rounded px-2 py-1 text-xs mb-1"
                                   onChange={(e) => setSearch(e.target.value)} />
                            <div className="flex justify-between text-[11px] mb-1">
                              <button className="text-emerald-700 hover:underline"
                                      onClick={() => setSel((s) => ({ ...s, [col.key]: shown }))}>
                                Select all
                              </button>
                              <button className="text-zinc-500 hover:underline"
                                      onClick={() => setSel((s) => ({ ...s, [col.key]: [] }))}>
                                Clear
                              </button>
                            </div>
                            <div className="max-h-52 overflow-y-auto">
                              {shown.length === 0 && (
                                <p className="text-[11px] text-zinc-400 py-2">No matching values.</p>
                              )}
                              {shown.map((v) => (
                                <label key={v || "(blank)"}
                                       className="flex items-center gap-2 py-0.5 text-xs cursor-pointer hover:bg-zinc-50 rounded px-1">
                                  <input type="checkbox"
                                         checked={chosen.includes(v)}
                                         onChange={() => toggle(col, v)} />
                                  <span className={v === "" ? "text-zinc-400 italic" : ""}>
                                    {v === "" ? "(blank)" : v}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={rowKey(r)}
                  className={`border-t border-zinc-100 ${onRowClick ? "hover:bg-zinc-50" : ""}`}>
                {columns.map((col) => (
                  <td key={col.key}
                      className={`px-3 py-2 align-top ${col.align === "right" ? "text-right tabular-nums" : ""}`}>
                    {col.render ? col.render(r) : dash(valueOf(col, r))}
                  </td>
                ))}
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={columns.length}
                    className="px-3 py-8 text-center text-sm text-zinc-500">
                  {rows.length === 0 ? emptyText : "No rows match these filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
